import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';

function LLMPage() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([
    {
      role: 'assistant',
      content: 'This model reads the text you provide and continues it, a very basic transformer predicting the next words.'
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('model1');
  const [throughputMetric, setThroughputMetric] = useState<number | null>(null);
  const completionEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const modelEndpoints = {
    model1: 'https://www.aidenzhang.dev/run_inference_s/',
    model2: 'https://www.aidenzhang.dev/run_inference_w/',
    model3: 'https://www.aidenzhang.dev/run_inference_2_5/'
  };

  useEffect(() => {
    completionEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, loading]);

  const countDiscreteTokens = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
  };

  const calculatePercentile = (values: number[], percentile: number) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil((percentile / 100) * sorted.length) - 1));
    return sorted[index];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    const userInput = prompt.trim();
    setMessages(prev => [...prev, { role: 'user', content: userInput }]);
    setPrompt('');
    setLoading(true);
    setThroughputMetric(null);

    try {
      const requestedModel = selectedModel as keyof typeof modelEndpoints;
      let endpoint = modelEndpoints[requestedModel];
      let res = await fetch(`${endpoint}${encodeURIComponent(userInput)}`);

      if (!res.ok && requestedModel === 'model3' && (res.status === 403 || res.status === 404)) {
        console.warn(`Model 3 endpoint failed with ${res.status}; retrying with the working model 2 endpoint.`);
        endpoint = modelEndpoints.model2;
        res = await fetch(`${endpoint}${encodeURIComponent(userInput)}`);
      }

      if (!res.ok) throw new Error(`Server Error ${res.status}`);

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      const chunkRates: number[] = [];
      const streamStartTime = performance.now();
      let previousChunkTime = streamStartTime;

      if (!reader) throw new Error('No reader available');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const now = performance.now();
        const deltaMs = Math.max(1, now - previousChunkTime);
        const discreteTokens = countDiscreteTokens(chunk);

        if (discreteTokens > 0) {
          const rate = discreteTokens / (deltaMs / 1000);
          chunkRates.push(rate);
          setThroughputMetric(calculatePercentile(chunkRates, 95));
        }

        previousChunkTime = now;
        accumulatedText += chunk;
        setMessages(prev => {
          const next = [...prev];
          const lastMessage = next[next.length - 1];
          if (lastMessage?.role === 'assistant') {
            next[next.length - 1] = { ...lastMessage, content: accumulatedText };
          } else {
            next.push({ role: 'assistant', content: accumulatedText });
          }
          return next;
        });
      }

      setThroughputMetric(chunkRates.length > 0 ? calculatePercentile(chunkRates, 95) : 0);
    } catch (err) {
      console.error('Fetch error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err}` }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <button onClick={() => navigate('/')} className="llm-button">
          ← back
        </button>
        <div className="llm-model-picker">
          <button
            type="button"
            className={`llm-model-pill ${selectedModel === 'model1' ? 'active' : ''}`}
            onClick={() => setSelectedModel('model1')}
          >
            Aiden-GPT 1
          </button>
          <button
            type="button"
            className={`llm-model-pill ${selectedModel === 'model2' ? 'active' : ''}`}
            onClick={() => setSelectedModel('model2')}
          >
            Aiden-GPT 2
          </button>
          <button
            type="button"
            className={`llm-model-pill ${selectedModel === 'model3' ? 'active' : ''}`}
            onClick={() => setSelectedModel('model3')}
          >
            Aiden-GPT 2.5
            <span className="llm-model-new">new</span>
          </button>
        </div>
      </header>

      <div className="llm-shell">
        <main className="llm-main">
          <section className="llm-model-note">
            <strong>2.5</strong> includes all new min-p sampling, KV cache, no-repeat penalties, and a host of speed improvements but it is still the same weights under the hood(3.0 should have more training)
          </section>
          <section className="llm-output-card">
            <div className="llm-output-header">
              <h3>Conversation</h3>
              <div className="llm-output-header-meta">
                <span className="llm-metric-pill">
                  p95 tok/s: {throughputMetric === null ? '—' : throughputMetric.toFixed(1)}
                </span>
                <span>{loading ? 'Streaming…' : 'Ready'}</span>
              </div>
            </div>
            <div className="llm-output">
              {messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={`llm-message ${message.role}`}>
                  <strong>{message.role === 'user' ? 'You' : 'Model'}</strong>
                  <p>{message.content}</p>
                </div>
              ))}
              <div ref={completionEndRef} />
            </div>
          </section>

          <section className="llm-card">
            <form onSubmit={handleSubmit} className="llm-form">
              <label className="llm-label" htmlFor="prompt-input">Prompt</label>
              <textarea
                id="prompt-input"
                ref={inputRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="llm-textarea"
                placeholder="give it some text (be warned it is not the smartest). The context window is quite small, and lacks persistence across prompts. Please give it around 2 seconds for lambda cold start"
                rows={4}
                disabled={loading}
                autoFocus
              />
              <div className="llm-form-actions">
                <button type="submit" className="llm-submit" disabled={loading || !prompt.trim()}>
                  {loading ? 'Generating…' : 'Continue text'}
                </button>
              </div>
            </form>
          </section>
        </main>
      </div>

      <section className="llm-details-card">
        <div className="llm-details-header">
          <h3>Implementation highlights</h3>
          <span>custom transformer stack</span>
        </div>
        <ul className="llm-details-list">
          <li>Implemented a 27 million parameter decoder-only Transformer end to end, <strong>by hand</strong>, with no autograd framework: 4-layer, 4-head causal self-attention (d_model 384, 256-token context), MLP blocks, residual + layer norm, KV caching, and top-k/min-p sampling.</li>
          <li><strong>Hand-derived</strong> and implemented backward passes for attention, softmax, LayerNorm, feed-forward layers, and the full LLM training pipeline.</li>
          <li>Built a BPE tokenizer (25K vocab) trained in under 30 min, plus a preprocessing pipeline handling 1M+ Wikipedia articles on consumer hardware.</li>
          <li>Wrote parallel CuPy and MLX training pipelines with batched matmuls, activation caching, and AdamW.</li>
          <li>Trained on 205 million tokens (25K steps) on EC2.</li>
        </ul>
      </section>
    </div>
  );
}

export default LLMPage;
